import { useQuery } from 'react-query'
import { useSelector } from 'react-redux'

import { currentHiringTeam } from '@/core/selectors/sessions'

import { getApplicationCount } from './api'
import { buildApplicationCountCacheKey } from './helpers'

/**
 * Hook for fetching the application count for the current hiring team.
 *
 * @param {Object} [options] - The options for the hook.
 * @param {boolean} [options.enablePolling] - Flag indicating whether to enable polling for automatic data updates every 5 minutes.
 *
 * @returns {Object} - An object containing the application count and loading state.
 * @property {number} count - The application count for the hiring team.
 * @property {boolean} isLoading - Flag indicating whether the data is currently being loaded.
 *
 * @example
 * // Usage example
 * const { count, isLoading } = useFetchApplicationCount({ enablePolling: true });
 */
export const useFetchApplicationCount = ({ enablePolling } = {}) => {
  const hiringTeam = useSelector(currentHiringTeam)
  const hiringTeamId = hiringTeam?.id

  const { data, isLoading } = useQuery(buildApplicationCountCacheKey({ hiringTeamId }), () => getApplicationCount(hiringTeamId), {
    // Poll every 5 minutes to keep the count up to date.
    // Shouldn't need a smaller interval since a refetch will usually have been triggered by another action, e.g.
    //   - component being rerendered
    //   - RQ fetching on window focus
    refetchInterval: enablePolling ? 300000 : null,
  })

  return {
    count: data?.hiringTeam?.applicationListCount,
    isLoading,
  }
}
